
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";

export interface AIAnalysisResult {
  globalAnalysis: {
    summary: string;
    strengths: string[];
    weaknesses: string[];
    trends: string;
    overallScore: number;
  };
  levelAnalysis?: {
    level1?: {
      performance: string;
      averageScore: number;
      recommendations: string[];
    };
    level2?: {
      performance: string;
      averageScore: number;
      recommendations: string[];
    };
    level3?: {
      performance: string;
      averageScore: number;
      recommendations: string[];
    };
  };
  exerciseAnalysis?: {
    putting?: {
      performance: string;
      accuracy: number;
      consistency: number;
      recommendations: string[];
    };
    chipping?: {
      performance: string;
      accuracy: number;
      consistency: number;
      recommendations: string[];
    };
    driving?: {
      performance: string;
      accuracy: number;
      distance: number;
      recommendations: string[];
    };
  };
  actionPlan?: {
    priority1: string;
    priority2: string;
    priority3: string;
    weeklyGoals: string[];
    monthlyGoals: string[];
  };
}

export const useAIAnalysis = () => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<AIAnalysisResult | null>(null);
  const [analysisDate, setAnalysisDate] = useState<string | null>(null);
  const [canAnalyze, setCanAnalyze] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  const { i18n } = useTranslation();

  useEffect(() => {
    loadExistingAnalysis();
  }, []);

  const loadExistingAnalysis = async () => {
    try {
      setIsLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Vérifier s'il existe une analyse récente (cette semaine)
      const { data: recentAnalysis, error } = await supabase
        .from("ai_analysis_results")
        .select("*")
        .eq("user_id", user.id)
        .eq("analysis_type", "performance_analysis")
        .gte("created_at", new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString())
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (!error && recentAnalysis) {
        setAnalysisResult(recentAnalysis.recommendations as unknown as AIAnalysisResult);
        setAnalysisDate(recentAnalysis.created_at);
        setCanAnalyze(false);
      } else {
        // Vérifier si l'utilisateur peut faire une nouvelle analyse
        const canUse = await checkAnalysisPermission('performance_analysis');
        setCanAnalyze(canUse);
      }
    } catch (error) {
      console.error('Error loading existing analysis:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const checkAnalysisPermission = async (analysisType: string) => {
    try {
      const { data, error } = await supabase.rpc('can_use_analysis_type', {
        analysis_type_param: analysisType
      });
      
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error checking analysis permission:', error);
      return false;
    }
  };

  const recordAnalysisUsage = async (analysisType: string) => {
    try {
      const { error } = await supabase.rpc('record_analysis_usage', {
        analysis_type_param: analysisType
      });
      
      if (error) throw error;
    } catch (error) {
      console.error('Error recording analysis usage:', error);
    }
  };

  const analyzeTrainingData = async () => {
    try {
      setIsAnalyzing(true);

      // Vérifier si l'utilisateur peut utiliser l'analyse cette semaine
      if (!canAnalyze) {
        toast({
          variant: "destructive",
          title: "Limite atteinte",
          description: "Vous avez déjà utilisé l'analyse IA cette semaine. Prochaine utilisation possible dans 7 jours.",
        });
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          variant: "destructive",
          title: "Erreur",
          description: "Vous devez être connecté pour utiliser l'analyse IA.",
        });
        return;
      }

      // Récupérer les données d'entraînement
      const [levelResults, individualScores, swingTendencies] = await Promise.all([
        supabase
          .from("level_tests_results")
          .select("*")
          .eq("user_id", user.id)
          .order("test_date", { ascending: false }),
        supabase
          .from("individual_scores")
          .select("*")
          .eq("user_id", user.id)
          .order("created_at", { ascending: false }),
        supabase
          .from("swing_tendencies")
          .select("*")
          .eq("user_id", user.id)
          .order("test_date", { ascending: false })
      ]);

      if (levelResults.error || individualScores.error || swingTendencies.error) {
        throw new Error("Erreur lors de la récupération des données");
      }

      const trainingData = {
        levelResults: levelResults.data || [],
        individualScores: individualScores.data || [],
        swingTendencies: swingTendencies.data || []
      };

      // Vérifier qu'il y a des données à analyser
      if (trainingData.levelResults.length === 0 && 
          trainingData.individualScores.length === 0 && 
          trainingData.swingTendencies.length === 0) {
        toast({
          variant: "destructive",
          title: "Pas de données",
          description: "Aucune donnée d'entraînement trouvée. Effectuez quelques exercices d'abord.",
        });
        return;
      }

      // Appeler l'edge function d'analyse IA
      const { data, error } = await supabase.functions.invoke('ai-analysis', {
        body: { 
          trainingData,
          language: i18n.language 
        }
      });

      if (error) throw error;

      // Enregistrer l'utilisation de l'analyse
      await recordAnalysisUsage('performance_analysis');

      // Sauvegarder le résultat dans la base de données
      const { error: saveError } = await supabase
        .from("ai_analysis_results")
        .insert({
          user_id: user.id,
          analysis_type: 'performance_analysis',
          analysis_data: trainingData,
          insights: data.analysis.globalAnalysis.summary,
          recommendations: data.analysis
        });

      if (saveError) {
        console.error('Error saving analysis result:', saveError);
      }

      setAnalysisResult(data.analysis);
      setAnalysisDate(new Date().toISOString());
      setCanAnalyze(false);
      
      toast({
        title: "Analyse terminée",
        description: "Votre analyse IA est maintenant disponible.",
      });

    } catch (error: any) {
      console.error('Error during AI analysis:', error);
      toast({
        variant: "destructive",
        title: "Erreur d'analyse",
        description: error.message || "Une erreur s'est produite lors de l'analyse.",
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  return {
    isAnalyzing,
    analysisResult,
    analysisDate,
    analyzeTrainingData,
    setAnalysisResult,
    setAnalysisDate,
    canAnalyze,
    isLoading
  };
};
